class IndulgeoReviews extends HTMLElement {
  connectedCallback() {
    this.source = this.dataset.api.replace(/\/$/, '');
    this.isStatic = /\.json(?:\?|$)/i.test(this.source);
    this.limit = Number(this.dataset.limit || 24);
    this.page = 1;
    this.controller = null;
    this.loading = false;
    this.requestId = 0;
    this.staticReviewsPromise = null;
    this.list = this.querySelector('[data-review-list]');
    this.status = this.querySelector('[data-review-status]');
    this.count = this.querySelector('[data-review-count]');
    this.more = this.querySelector('[data-load-more]');
    this.form = this.querySelector('[data-review-filters]');
    this.more.addEventListener('click', () => this.load(false));
    this.form.addEventListener('change', () => this.reset());
    let timer;
    this.form.elements.search.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.reset(), 300);
    });
    this.load(true);
  }

  reset() {
    this.controller?.abort();
    this.requestId += 1;
    this.loading = false;
    this.page = 1;
    this.load(true);
  }

  parameters() {
    const values = new FormData(this.form);
    const params = new URLSearchParams({ page: String(this.page), limit: String(this.limit) });
    for (const [key, value] of values) if (String(value).trim()) params.set(key, String(value));
    return params;
  }

  // parameters() {
  //   const values = new FormData(this.form);
  //   const params = new URLSearchParams({ page: String(this.page), limit: String(this.limit) });
  //   for (const [key, value] of values) {
  //     let v = String(value).trim();
  //     if (!v) continue;
  //     if (key === 'product') {
  //       const match = v.match(/\/products\/([^/?#]+)/);
  //       if (match) v = match[1];
  //     }
  //     params.set(key, v);
  //   }
  //   return params;
  // }

  async load(replace) {
    if (this.loading) return;
    this.loading = true;
    const requestId = ++this.requestId;
    this.controller = this.isStatic ? null : new AbortController();
    this.more.disabled = true;
    this.status.textContent = 'Loading reviews\u2026';
    const params = this.parameters();
    try {
      const data = this.isStatic
        ? await this.loadStatic(params)
        : await this.loadApi(params, this.controller.signal);
      if (requestId !== this.requestId) return;
      if (replace) this.list.replaceChildren();
      data.reviews.forEach((review) => this.list.append(this.card(review)));
      this.count.textContent = data.total.toLocaleString();
      this.status.textContent =
        data.total === 0
          ? 'No reviews match these filters.'
          : `${this.list.children.length.toLocaleString()} of ${data.total.toLocaleString()} reviews shown`;
      this.page += 1;
      this.more.hidden = this.page > data.totalPages;
    } catch (error) {
      if (requestId === this.requestId && error.name !== 'AbortError') {
        this.status.textContent =
          'We could not load reviews. Check that the JSON URL is public and allows cross-origin requests.';
      }
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        this.more.disabled = false;
      }
    }
  }

  async loadApi(params, signal) {
    const response = await fetch(`${this.source}/api/reviews?${params}`, {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Reviews could not be loaded (${response.status})`);
    return response.json();
  }

  async loadStatic(params) {
    if (!this.staticReviewsPromise) {
      this.staticReviewsPromise = fetch(this.source, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Reviews could not be loaded (${response.status})`);
          return response.json();
        })
        .then((dataset) => {
          const reviews = Array.isArray(dataset) ? dataset : dataset.reviews;
          if (!Array.isArray(reviews))
            throw new Error('The JSON file does not contain a reviews array');
          return reviews;
        });
    }
    const allReviews = await this.staticReviewsPromise;
    const rating = Number(params.get('rating')) || null;
    const product = params.get('product')?.toLocaleLowerCase();
    const search = params.get('search')?.toLocaleLowerCase();
    const sort = params.get('sort') || 'recent';
    const filtered = allReviews.filter((review) => {
      const matchesRating = rating === null || Math.round(Number(review.rating)) === rating;
      const productTitle = String(review.product?.title || '').toLocaleLowerCase();
      const matchesProduct = !product || productTitle.includes(product);
      const searchable = [review.title, review.body, review.reviewer?.name, review.product?.title]
        .map((value) => String(value || '').toLocaleLowerCase())
        .join(' ');
      return matchesRating && matchesProduct && (!search || searchable.includes(search));
    });

    // const rating = Number(params.get('rating')) || null;
    // const product = params.get('product')?.toLocaleLowerCase();
    // const search = params.get('search')?.toLocaleLowerCase();
    // const sort = params.get('sort') || 'recent';
    // const filtered = allReviews.filter((review) => {
    //   const matchesRating = rating === null || Math.round(Number(review.rating)) === rating;
    //   const handle = String(review.product?.handle || '').toLocaleLowerCase();
    //   const matchesProduct = !product || handle === product;
    //   const searchable = [review.title, review.body, review.reviewer?.name, review.product?.title]
    //     .map((value) => String(value || '').toLocaleLowerCase())
    //     .join(' ');
    //   return matchesRating && matchesProduct && (!search || searchable.includes(search));
    // });

    const timestamp = (review) => Date.parse(review.createdAt || review.created_at || '') || 0;
    filtered.sort((a, b) => {
      if (sort === 'oldest') return timestamp(a) - timestamp(b);
      if (sort === 'highest')
        return Number(b.rating) - Number(a.rating) || timestamp(b) - timestamp(a);
      if (sort === 'lowest')
        return Number(a.rating) - Number(b.rating) || timestamp(b) - timestamp(a);
      if (sort === 'helpful')
        return (
          Number(b.helpfulCount || 0) - Number(a.helpfulCount || 0) || timestamp(b) - timestamp(a)
        );
      return timestamp(b) - timestamp(a);
    });
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || this.limit;
    const start = (page - 1) * limit;
    return {
      page,
      limit,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / limit),
      reviews: filtered.slice(start, start + limit),
    };
  }

  card(review) {
    const article = document.createElement('article');
    article.className = 'ir-card';
    const stars = document.createElement('div');
    stars.className = 'ir-stars';
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    stars.textContent =
      '\u2605'.repeat(Math.round(review.rating)) + '\u2606'.repeat(5 - Math.round(review.rating));
    article.append(stars);
    if (review.title) article.append(this.element('h3', 'ir-title', review.title));
    article.append(this.element('p', 'ir-body', review.body || ''));
    if (review.images?.length) {
      const gallery = document.createElement('div');
      gallery.className = 'ir-images';
      review.images.forEach((image) => {
        const img = document.createElement('img');
        img.src = image.thumbnailUrl || image.url;
        img.alt = 'Customer review photo';
        img.loading = 'lazy';
        gallery.append(img);
      });
      article.append(gallery);
    }
    const meta = document.createElement('footer');
    meta.className = 'ir-meta';
    meta.append(this.element('span', '', review.reviewer?.name || 'Anonymous'));
    if (review.reviewer?.verifiedBuyer) {
      meta.append(this.element('span', 'ir-verified', '\u2713 Verified buyer'));
    }
    if (review.product?.title) meta.append(this.element('span', '', review.product.title));
    const createdAt = review.createdAt || review.created_at;
    if (createdAt) meta.append(this.element('time', '', new Date(createdAt).toLocaleDateString()));
    article.append(meta);
    return article;
  }

  element(tag, className, content) {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = content;
    return node;
  }
}
if (!customElements.get('indulgeo-reviews')) {
  customElements.define('indulgeo-reviews', IndulgeoReviews);
}
